/**
 * @fileoverview Orchestrator for interactive team chat.
 * Renders the welcome -> form -> chat flow outside of the Chat component,
 * keeping Chat free of interactive concerns.
 */

import { useState } from "react";
import { Chat, type ChatProps } from "@/components/chat/chat";
import { InteractiveChatProvider } from "./interactive-context";
import { InteractiveFormStep } from "./form-step";
import { InteractiveWelcomeScreen } from "./welcome-screen";
import { useInteractiveOnboarding } from "@/hooks/use-interactive-onboarding";
import type { Team } from "@/types/team-types";

interface InteractiveChatProps {
  /** The team this interactive chat belongs to */
  team: Team;
  /** All Chat props except interactiveChat (which is always true here) */
  chatProps: Omit<ChatProps, "initialMessage">;
}

/**
 * Wraps the Chat component with the interactive onboarding flow.
 * Renders welcome screen, intake form, or Chat depending on phase.
 */
export function InteractiveChat({ team, chatProps }: InteractiveChatProps) {
  const [initialMessage, setInitialMessage] = useState<string | undefined>(undefined);

  const {
    phase,
    formSchema,
    formName,
    isLoading,
    advanceFromWelcome,
    submitForm,
  } = useInteractiveOnboarding({
    teamId: team.id,
    hasExistingMessages: (chatProps.initialMessages?.length ?? 0) > 0,
  });

  if (isLoading) {
    return (
      <div className="flex size-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (phase === "welcome") {
    return (
      <InteractiveWelcomeScreen
        teamName={team.name}
        teamDescription={team.description ?? undefined}
        teamIcon={team.icon ?? undefined}
        onGetStarted={advanceFromWelcome}
      />
    );
  }

  if (phase === "form" && formSchema) {
    return (
      <InteractiveFormStep
        teamId={team.id}
        formSchema={formSchema}
        formName={formName || "Intake Form"}
        onSubmit={async (values) => {
          const message = await submitForm(values);
          setInitialMessage(message);
        }}
        isSubmitting={false}
      />
    );
  }

  // Phase === "chat" — render Chat wrapped in InteractiveChatProvider
  return (
    <InteractiveChatProvider enabled>
      <Chat
        {...chatProps}
        initialMessage={initialMessage}
      />
    </InteractiveChatProvider>
  );
}
